import { deserialize, JsonProperty, Serializable } from 'ts-jackson'
import { REFERRAL_CHANNEL } from '@/configs'
import { Fromable } from '../model'

export enum CampaignStatus {
  ACTIVE = 'ACTIVE',
  PAUSED = 'PAUSED',
  ENDED = 'ENDED',
}

export enum CampaignStatusFilter {
  ALL = 'ALL',
  ACTIVE = 'ACTIVE',
  PAUSED = 'PAUSED',
  ENDED = 'ENDED',
}

@Serializable()
export class CampaignOwner extends Fromable {
  @JsonProperty('id')
  id!: number

  @JsonProperty('name')
  name!: string
}

@Serializable()
export class Campaign extends Fromable {
  @JsonProperty('id')
  id!: string

  @JsonProperty('code')
  code!: string

  @JsonProperty('name')
  name!: string

  @JsonProperty('channels')
  channels!: REFERRAL_CHANNEL[]

  @JsonProperty('status')
  status!: CampaignStatus

  @JsonProperty('startDate')
  startDate?: string | null

  @JsonProperty('endDate')
  endDate?: string | null

  @JsonProperty('budget')
  budget?: number | null

  /** Sum of spend log entries (preferred over `budget` when populated). */
  @JsonProperty('totalSpend')
  totalSpend?: number | null

  @JsonProperty('targetLeads')
  targetLeads?: number | null

  @JsonProperty('targetCpl')
  targetCpl?: number | null

  @JsonProperty('owner')
  owner?: CampaignOwner | null

  @JsonProperty('leadCount')
  leadCount?: number

  @JsonProperty('memberCount')
  memberCount?: number

  @JsonProperty('totalRevenue')
  totalRevenue?: number | null

  @JsonProperty('cpl')
  cpl?: number | null

  /** Cost per member (spend ÷ members); null when spend or members = 0. */
  @JsonProperty('cac')
  cac?: number | null

  @JsonProperty('roas')
  roas?: number | null

  @JsonProperty('createdAt')
  createdAt!: string

  @JsonProperty('updatedAt')
  updatedAt!: string
}

@Serializable()
export class CampaignSpend extends Fromable {
  @JsonProperty('id')
  id!: string

  @JsonProperty('campaignId')
  campaignId!: string

  @JsonProperty('date')
  date!: string

  @JsonProperty('amount')
  amount!: number

  @JsonProperty('note')
  note?: string | null

  @JsonProperty('createdAt')
  createdAt!: string
}

@Serializable()
export class CampaignFunnel extends Fromable {
  @JsonProperty('campaignId')
  campaignId!: string

  @JsonProperty('leadCount')
  leadCount!: number

  @JsonProperty('visitCount')
  visitCount!: number

  @JsonProperty('trialCount')
  trialCount!: number

  @JsonProperty('memberCount')
  memberCount!: number
}

@Serializable()
export class CampaignAttributedLead extends Fromable {
  @JsonProperty('id')
  id!: string

  @JsonProperty('campaignId')
  campaignId!: string

  @JsonProperty('contactName')
  contactName!: string

  @JsonProperty('phone')
  phone!: string

  @JsonProperty('status')
  status!: string

  @JsonProperty('createdAt')
  createdAt!: string
}

export type FunnelStageKey = 'LEAD' | 'VISIT' | 'TRIAL' | 'MEMBER'

@Serializable()
export class CampaignReportRow extends Fromable {
  @JsonProperty('id')
  id!: string

  @JsonProperty('code')
  code!: string

  @JsonProperty('name')
  name!: string

  @JsonProperty('channels')
  channels!: REFERRAL_CHANNEL[]

  @JsonProperty('status')
  status!: CampaignStatus

  @JsonProperty('totalSpend')
  totalSpend!: number

  @JsonProperty('budget')
  budget!: number | null

  @JsonProperty('leadCount')
  leadCount!: number

  @JsonProperty('memberCount')
  memberCount!: number

  @JsonProperty('revenue')
  revenue!: number

  @JsonProperty('cpl')
  cpl!: number | null

  @JsonProperty('cac')
  cac!: number | null

  @JsonProperty('roas')
  roas!: number | null

  @JsonProperty('conversionRate')
  conversionRate!: number | null
}

@Serializable()
export class CampaignReportSummary extends Fromable {
  @JsonProperty('totalSpend')
  totalSpend!: number

  @JsonProperty('totalLeads')
  totalLeads!: number

  @JsonProperty('totalMembers')
  totalMembers!: number

  @JsonProperty('totalRevenue')
  totalRevenue!: number

  @JsonProperty('avgCpl')
  avgCpl!: number | null

  @JsonProperty('avgCac')
  avgCac!: number | null

  @JsonProperty('avgRoas')
  avgRoas!: number | null

  @JsonProperty('conversionRate')
  conversionRate!: number | null
}

@Serializable()
export class CampaignMonthlyPoint extends Fromable {
  @JsonProperty('month')
  month!: string

  @JsonProperty('spend')
  spend!: number

  @JsonProperty('leads')
  leads!: number

  @JsonProperty('revenue')
  revenue!: number
}

@Serializable()
export class CampaignChannelBreakdown extends Fromable {
  @JsonProperty('channel')
  channel!: REFERRAL_CHANNEL

  @JsonProperty('spend')
  spend!: number

  @JsonProperty('leads')
  leads!: number
}

@Serializable()
export class CampaignFunnelStage extends Fromable {
  @JsonProperty('stage')
  stage!: FunnelStageKey

  @JsonProperty('count')
  count!: number
}

@Serializable()
export class CampaignReportResult extends Fromable {
  @JsonProperty({ path: 'summary', type: CampaignReportSummary })
  summary!: CampaignReportSummary

  @JsonProperty({
    path: 'rows',
    elementType: CampaignReportRow,
    deserialize: (value: unknown): CampaignReportRow[] =>
      Array.isArray(value)
        ? value.map((v) => deserialize(v as Record<string, unknown>, CampaignReportRow))
        : [],
  })
  rows!: CampaignReportRow[]

  @JsonProperty({
    path: 'channelBreakdown',
    elementType: CampaignChannelBreakdown,
    deserialize: (value: unknown): CampaignChannelBreakdown[] =>
      Array.isArray(value)
        ? value.map((v) => deserialize(v as Record<string, unknown>, CampaignChannelBreakdown))
        : [],
  })
  channelBreakdown!: CampaignChannelBreakdown[]

  @JsonProperty({
    path: 'funnel',
    elementType: CampaignFunnelStage,
    deserialize: (value: unknown): CampaignFunnelStage[] =>
      Array.isArray(value)
        ? value.map((v) => deserialize(v as Record<string, unknown>, CampaignFunnelStage))
        : [],
  })
  funnel!: CampaignFunnelStage[]

  @JsonProperty({
    path: 'monthly',
    elementType: CampaignMonthlyPoint,
    deserialize: (value: unknown): CampaignMonthlyPoint[] =>
      Array.isArray(value)
        ? value.map((v) => deserialize(v as Record<string, unknown>, CampaignMonthlyPoint))
        : [],
  })
  monthly!: CampaignMonthlyPoint[]
}

export interface CreateCampaignPayload {
  code: string
  name: string
  channels: REFERRAL_CHANNEL[]
  status?: CampaignStatus
  // Required: the campaign window bounds actual-revenue attribution on the BE.
  startDate: string
  endDate: string
  budget?: number | null
  targetLeads?: number | null
  targetCpl?: number | null
  ownerId?: number | null
}

export type UpdateCampaignPayload = Partial<CreateCampaignPayload>

export interface QuickCreateCampaignPayload {
  code: string
  name: string
  channels: REFERRAL_CHANNEL[]
  startDate: string
  endDate: string
}

export interface CreateSpendPayload {
  date: string
  amount: number
  note?: string | null
}

export type UpdateSpendPayload = Partial<CreateSpendPayload>

export interface CampaignListFilter {
  page?: number
  pageSize?: number
  query?: string
  channel?: REFERRAL_CHANNEL
  status?: CampaignStatusFilter
}

export interface CampaignReportFilter {
  startDate?: string
  endDate?: string
  channel?: REFERRAL_CHANNEL
  campaignId?: string
}
